import React, {Dispatch, SetStateAction, useEffect} from "react";
import {useSelector} from "react-redux";
import timerange from "../../store/reducers/timerange";
import {MultiMetricAlert} from "../../pages/alerts/MultiMetricAlert";
import {MultiMetoroMetricsChart, MultiMetoroMetricsChartProps} from "../Charts/MultiMetoroMetricsCharts";
import {ChartType} from "../Charts/MetoroChart";
import {
    defaultMetricQueryDisplaySettingsForAlert,
    MetricQuerySelectorPanel
} from "../Dashboarding/widgets/MultiMetricSelector";
import {EventSelector, getGraphAnnotationProperties, getWindowUnitSeconds} from "./AlertMetricSelector";
import {AlertCircle} from "lucide-react";
import {Alert as AlertUI} from "../ui/alert";
import {Alert, ThresholdComparator} from "../../pages/alerts/model";
import {AggregateMonitorEvaluationPayload, EvalType} from "../../pages/alerts/MetricAlert";
import {getAggregateStartEndTime, StatChart} from "../Charts/StatChart";
import {ReduceOption} from "../Dashboarding/internalwidgets";

export const round = (number: number, step: number) => {
    return Math.round(number / step) * step;
};

export function AlertMultiMetricSelector(props: {
    alert: Alert;
    setAlert: Dispatch<SetStateAction<Alert>>;
    setIsValid?: (isValid: boolean) => void;
}) {
    const timeRange = useSelector(timerange.selectors.getTimeRange);
    const typedAlert = props.alert.alert as MultiMetricAlert;
    const [chartProps, setChartProps] = React.useState<MultiMetoroMetricsChartProps>(initialChartProps());
    const [validationError, setValidationError] = React.useState<string | undefined>();

    function initialChartProps(): MultiMetoroMetricsChartProps {
        return {
            startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
            endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000),
            metricSpecifiers: typedAlert.filters.metricSpecifiers,
            formulas: typedAlert.filters.formula ? [typedAlert.filters.formula] : [],
            metoroQLQueries: typedAlert.filters.metoroQlQuery ? [typedAlert.filters.metoroQlQuery] : [],
            type: ChartType.Line,
            isMetoroQlSelector: !!typedAlert.filters.metoroQlQuery
        }
    }

    // Validate that we have exactly one timeseries
    useEffect(() => {
        const visibleMetrics = chartProps.metricSpecifiers.filter(m => !m.shouldNotReturn).length;
        const formulaCount = chartProps.formulas?.length || 0;
        const metoroQLQueryLength = chartProps.metoroQLQueries?.length || 0;

        if (visibleMetrics + formulaCount + metoroQLQueryLength === 0) {
            setValidationError("You must have either one visible metric or one formula for the alert");
            props.setIsValid?.(false);
        } else if (visibleMetrics + formulaCount + metoroQLQueryLength > 1) {
            setValidationError("You can only have one visible metric or one formula for the alert");
            props.setIsValid?.(false);
        } else {
            setValidationError(undefined);
            props.setIsValid?.(true);
        }
    }, [chartProps.metricSpecifiers, chartProps.formulas, chartProps.metoroQLQueries]);

    useEffect(() => {
        setChartProps((prev) => {
            return {
                ...prev,
                startTime: Math.floor(timeRange.getStartEnd()[0].getTime() / 1000),
                endTime: Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)
            };
        });
    }, [timeRange]);

    // Add threshold visualization
    useEffect(() => {
        let overrideStartTime = undefined;
        let overrideEndTime = undefined;
        if (typedAlert.monitorEvaluation.monitorEvaluationType == EvalType.Aggregate) {
            let monitorEvalutionPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
            let startEnd = getAggregateStartEndTime(chartProps.metricSpecifiers, Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)  - monitorEvalutionPayload.window * getWindowUnitSeconds(monitorEvalutionPayload.windowUnit), Math.floor(timeRange.getStartEnd()[1].getTime() / 1000))
            let bucketSize = chartProps.metricSpecifiers.length > 0 ? chartProps.metricSpecifiers[0].bucketSize! : chartProps.metoroQLQueries && chartProps.metoroQLQueries.length > 0 ? chartProps.metoroQLQueries[0].bucketSize! : 60
            overrideStartTime = round(startEnd[0], bucketSize)
            // This subtraction is so that the points line up
            overrideEndTime = round(startEnd[1], bucketSize) - bucketSize
        }

        const annotationProperties = getGraphAnnotationProperties(
            typedAlert.monitorEvaluation,
            timeRange,
            typedAlert.eventPayload.threshold,
            typedAlert.eventPayload.condition as ThresholdComparator,
            typedAlert.filters.metricSpecifiers.length > 0 ? typedAlert.filters.metricSpecifiers[0].bucketSize : undefined,
            overrideStartTime,
            overrideEndTime
        );
        const thresholdToSet = annotationProperties.thresholdToSet;
        const timePeriodToHighlight = annotationProperties.timePeriodToHighlight;

        setChartProps((prev) => {
            return {
                ...prev,
                threshold: thresholdToSet,
                timePeriodHighlight: timePeriodToHighlight,
                thresholdLabel: annotationProperties.annotationLabel
            }
        });
    }, [
        chartProps.metricSpecifiers.length,
        chartProps.metricSpecifiers.length > 0 ? chartProps.metricSpecifiers[0].bucketSize : undefined,
        typedAlert.monitorEvaluation.monitorEvaluationType,
        typedAlert.eventPayload.threshold,
        typedAlert.eventPayload.condition,
        typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Aggregate ? 
          (typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload)?.window : undefined,
        typedAlert.monitorEvaluation.monitorEvaluationType === EvalType.Aggregate ?
          (typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload)?.windowUnit : undefined,
        timeRange
    ]);

    useEffect(() => {
        if (validationError) return; // Don't update alert if there's a validation error

        props.setAlert((prev) => {
            let multiMetricAlert = prev.alert as MultiMetricAlert;
            let filters = {...multiMetricAlert.filters};
            filters.metricSpecifiers = chartProps.metricSpecifiers;
            filters.metoroQlQuery = chartProps.metoroQLQueries && chartProps.metoroQLQueries.length > 0 ? chartProps.metoroQLQueries[0] : undefined;
            filters.formula = chartProps.formulas && chartProps.formulas.length > 0 ? chartProps.formulas[0] : undefined;
            multiMetricAlert.filters = filters;
            
            // Set isMetoroQlSelector in chartProps whenever there's a metoroQlQuery
            setChartProps(prev => ({
                ...prev,
                isMetoroQlSelector: !!filters.metoroQlQuery
            }));
            
            return {...prev, alert: multiMetricAlert};
        });
    }, [
        validationError,
        chartProps.metricSpecifiers, 
        chartProps.formulas ? (chartProps.formulas.length > 0 ? chartProps.formulas[0] : null) : null,
        chartProps.metoroQLQueries ? (chartProps.metoroQLQueries.length > 0 ? chartProps.metoroQLQueries[0] : null) : null
    ]);

    return (
        <div className={"flex flex-col gap-8"}>
            <div className={"flex flex-col gap-4"}>
                {typedAlert.monitorEvaluation.monitorEvaluationType == EvalType.Aggregate &&
                    <div className={"grid grid-cols-2 gap-4"}>
                        <div className={"flex flex-col justify-center h-[300px]"}>
                            <StatChart
                                isSelecting={true}
                                mappings={
                                    [
                                        {
                                            value: typedAlert.eventPayload.threshold,
                                            operator: (() => {
                                                switch (typedAlert.eventPayload.condition) {
                                                    case ThresholdComparator.GreaterThan: return ">"
                                                    case ThresholdComparator.LessThan: return "<"
                                                    case ThresholdComparator.GreaterThanOrEqual: return ">="
                                                    case ThresholdComparator.LessThanOrEqual: return "<="
                                                }
                                            })(),
                                            type: "background",
                                            color: "#F44336"
                                        }
                                    ]
                                }
                                metricSpecifiers={chartProps.metricSpecifiers}
                                metoroQLQueries={chartProps.metoroQLQueries}
                                formulas={chartProps.formulas}
                                reduceOption={(typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload).evaluationFunction as ReduceOption}
                                precision={2}
                                startTime={(() => {
                                    let monitorEvalutionPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
                                    return getAggregateStartEndTime(chartProps.metricSpecifiers, chartProps.endTime - monitorEvalutionPayload.window * getWindowUnitSeconds(monitorEvalutionPayload.windowUnit), chartProps.endTime)[0]
                                })()}
                                endTime={(() => {
                                    let monitorEvalutionPayload = typedAlert.monitorEvaluation.monitorEvalutionPayload as AggregateMonitorEvaluationPayload;
                                    return getAggregateStartEndTime(chartProps.metricSpecifiers, chartProps.endTime - monitorEvalutionPayload.window * getWindowUnitSeconds(monitorEvalutionPayload.windowUnit), chartProps.endTime)[1]
                                })()}
                            />
                        </div>
                        <MultiMetoroMetricsChart
                            {...chartProps}
                            startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                            endTime={Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)}
                            hideLegend={false}
                            className={"flex flex-grow h-[300px]"}
                        />
                    </div>

                }
                {!(typedAlert.monitorEvaluation.monitorEvaluationType == EvalType.Aggregate) &&
                    <MultiMetoroMetricsChart
                        {...chartProps}
                        startTime={Math.floor(timeRange.getStartEnd()[0].getTime() / 1000)}
                        endTime={Math.floor(timeRange.getStartEnd()[1].getTime() / 1000)}
                        hideLegend={false}
                        className={"flex flex-grow h-[300px]"}
                    />}
                <MetricQuerySelectorPanel
                    isMetoroQlSelector={chartProps.isMetoroQlSelector}
                    hideAutoBucket={true}
                    metricSelectorDisplaySettings={
                        defaultMetricQueryDisplaySettingsForAlert
                    }
                    disableMultipleMetrics={chartProps.isMetoroQlSelector}
                    chartProps={chartProps}
                    setChartProps={setChartProps}
                />
                {validationError && (
                    <AlertUI className="mt-2 bg-red-400/10 border border-red-400 text-red-400">
                        <AlertCircle className="h-4 w-4"/>
                        <span className="ml-2">{validationError}</span>
                    </AlertUI>
                )}
            </div>
            {
                !validationError && (
                    <EventSelector
                        stepNumber={3}
                        alert={props.alert}
                        setAlert={props.setAlert}
                    />
                )
            }
        </div>
    )
        ;
} 